import type { BookingCalendar } from '../../types/appointments.js';
import type { BotConfig } from '../../types/leadBot.js';

// Which booking calendar a bot offers times from. ONE rule, used by the draft path (loadOffers), the
// archive guard and the migration, so they cannot disagree about it.

/** A calendar a bot may use: on its account, enabled, not archived. */
export function isUsableBotCalendar(calendar: BookingCalendar, accountId: string): boolean {
  return calendar.accountId === accountId && calendar.enabled && !calendar.archivedAt;
}

export type BotCalendarResolution =
  | { kind: 'calendar'; calendar: BookingCalendar; source: 'explicit' | 'only_one' }
  | { kind: 'none'; reason: 'no_calendars' | 'explicit_unusable' }
  | { kind: 'ambiguous'; count: number };

/**
 * Explicit `bookingCalendarId` wins and must itself be usable (a set-but-unusable choice offers NOTHING, it never
 * falls back to another calendar). Unset: exactly one usable calendar on the account = that one; several = ambiguous
 * (no times offered, one staff FYI); none = none.
 */
export function resolveBotCalendar(
  config: Pick<BotConfig, 'accountId' | 'bookingCalendarId'>,
  calendars: readonly BookingCalendar[],
): BotCalendarResolution {
  const usable = calendars.filter((c) => isUsableBotCalendar(c, config.accountId));
  const explicitId = config.bookingCalendarId ?? null;
  if (explicitId) {
    const hit = usable.find((c) => c.id === explicitId);
    return hit ? { kind: 'calendar', calendar: hit, source: 'explicit' } : { kind: 'none', reason: 'explicit_unusable' };
  }
  if (usable.length === 0) return { kind: 'none', reason: 'no_calendars' };
  if (usable.length === 1) return { kind: 'calendar', calendar: usable[0]!, source: 'only_one' };
  return { kind: 'ambiguous', count: usable.length };
}

/** Whether this bot is set to use `calendarId` today, explicitly or because it is the account's only live one. */
export function botUsesCalendar(
  config: Pick<BotConfig, 'accountId' | 'bookingCalendarId'> & { capabilities?: BotConfig['capabilities'] },
  calendarId: string,
  calendars: readonly BookingCalendar[],
): boolean {
  if (config.bookingCalendarId) return config.bookingCalendarId === calendarId;
  if (!(config.capabilities?.bookAppointments ?? false)) return false;
  const r = resolveBotCalendar(config, calendars);
  return r.kind === 'calendar' && r.calendar.id === calendarId;
}
